// 权限的四层模型（功能 / 列表 / 详情 / 操作），前后端共用同一份定义。
//
// 为什么要分层：一个权限码原来同时管"能不能进页面、能不能看列表、能不能编辑、能不能删除"，
// 管理员配不出"只读""只能看列表不给详情""给了操作自动带上浏览"这三种最常见的诉求。
// 现在每个菜单固定四层：
//   功能 X.page   进得去这个页面（左侧菜单显不显示）
//   列表 X.read   看得见列表
//   详情 X.detail 打得开单条详情（含日志、附件、成员）
//   操作 X.write  所有按钮（新建 / 编辑 / 删除 / 指派 / 提交 / 发送 …）
// 有审核语义的菜单再单独一条 X.approve（审核：同意 / 驳回 / 打款）—— 审核不算"操作"，
// 这样"只给审核、不给新建编辑"能做到，而且审核人的可见范围（能看全部单据）绑在审核码上更安全。
//
// 蕴含链：操作/审核 ⇒ 详情 ⇒ 列表 ⇒ 功能。只勾「操作」就自动能看列表、能进页面，
// 不会再出现"给了按钮却打不开页面"的坑；反向一律不给（只给列表的人不能编辑）。
/**
 * 「新建任务」的独立权限码。
 * 为什么单独一条：新建是"开单"，和"改单"是两件事 —— 成员可以照常改进度 / 写日志 / 邀请成员（task.write），
 * 但能不能**新建**任务由这条码决定（需求：新增必须是有特定权限的人才能新增）。
 * 它蕴含详情 / 列表 / 功能（勾了新建就能看到任务中心并打开自己建的那条），但不蕴含 task.write。
 */
export const TASK_CREATE_CODE = "task.create";
export const PERMISSION_MENUS = [
    {
        area: "dashboard", module: "工作台", menu: "工作台", sort: 10,
        page: "进入工作台", list: "查看工作台数据（看板、待办、最近动态）"
    },
    {
        // 风险事件页面已下线、并入任务中心：权限码 event.* 与接口（/api/events、event.*）全部保留，
        // 只是归属菜单改成「任务中心」，文案上说明它是任务中心里的**历史**风险事件（不再新建）。
        area: "event", module: "任务协作", menu: "任务中心", sort: 40,
        page: "进入任务中心（含历史风险事件）", list: "查看历史风险事件列表",
        detail: "查看历史风险事件详情（含日志、附件、成员）",
        action: "上报 / 编辑 / 指派 / 邀请成员 / 写日志 / 提交复核 / 取消事件（历史事件）"
    },
    {
        area: "risk", module: "风险管理", menu: "风控中心", sort: 30,
        page: "进入风控中心", list: "查看风控对象列表",
        detail: "查看风控对象详情（含日志）",
        action: "新建 / 编辑 / 删除风控对象"
    },
    {
        // 与 event 同属「任务中心」菜单：task 的 35-39 排在 event 的 40-44 前面，整块连续，不受值班 45 影响
        area: "task", module: "任务协作", menu: "任务中心", sort: 35,
        page: "进入任务中心", list: "查看任务列表",
        detail: "查看任务详情（含日志、附件、成员）",
        action: "编辑 / 指派 / 邀请成员 / 写日志 / 完成任务（新建由下面的「新建」单独授权）"
    },
    {
        area: "duty", module: "协作", menu: "值班管理", sort: 45,
        page: "进入值班管理", list: "查看值班月历与每天排班",
        detail: "查看某天值班表（地点 / 职位 / 班次明细）",
        action: "调整值班人员 / 根据上月生成排班 / 锁定排班"
    },
    {
        area: "approval", module: "审批与审计", menu: "审批", sort: 50,
        page: "进入审批", list: "查看审批列表", detail: "查看审批详情（关联事件与处理日志）",
        approve: "同意 / 驳回审批", approveCode: "event.approve"
    },
    {
        area: "audit", module: "审批与审计", menu: "操作日志", sort: 60,
        page: "进入操作日志", list: "查看操作日志列表", detail: "查看操作日志详情"
    },
    {
        // 文档中心已并入文件管理（文档一律以 .xlsx 呈现），文档的查看 / 编辑跟着文件权限走，
        // 不再单独有 document.* 权限码（老角色里的 document.* 会在启动时被清掉，见 permission-catalog）。
        area: "file", module: "文件与文档", menu: "文件管理", sort: 70,
        page: "进入文件管理", list: "查看文件与文档列表",
        detail: "查看 / 预览 / 下载文件与文档",
        action: "上传 / 改名 / 移动 / 共享 / 删除文件、新建 / 编辑 / 删除文档"
    },
    {
        area: "notice", module: "通知", menu: "公告", sort: 90,
        page: "进入公告", list: "查看公告列表", detail: "查看公告详情",
        action: "发布 / 编辑 / 删除公告"
    },
    {
        area: "telegram.chat", module: "通知", menu: "聊天记录", sort: 100,
        page: "进入聊天记录", list: "查看聊天记录", detail: "查看会话内容",
        action: "发消息 / 删除记录 / 立即归档"
    },
    {
        area: "telegram.group", module: "通知", menu: "群", sort: 110,
        page: "进入群管理", list: "查看 Telegram 群列表", detail: "查看群详情",
        action: "建群 / 改群 / 删群 / 向群发消息"
    },
    {
        area: "telegram.broadcast", module: "通知", menu: "群发", sort: 120,
        page: "进入定时群发", list: "查看定时群发列表", detail: "查看群发详情",
        action: "新建 / 编辑 / 删除 / 立即发送"
    },
    {
        area: "telegram.command", module: "通知", menu: "命令菜单", sort: 130,
        page: "进入命令菜单", list: "查看机器人命令", detail: "查看命令详情",
        action: "新建 / 编辑 / 删除命令 / 同步 Telegram 菜单"
    },
    {
        area: "report", module: "数据分析", menu: "统计报表", sort: 140,
        page: "进入统计报表", list: "查看报表",
        action: "新增 / 编辑 / 删除周期总结"
    },
    {
        area: "settings", module: "系统管理", menu: "系统配置", sort: 150,
        page: "进入系统配置", list: "查看系统配置", action: "修改系统配置"
    },
    {
        area: "user", module: "系统管理", menu: "用户管理", sort: 160,
        page: "进入用户管理", list: "查看用户列表", detail: "查看用户详情",
        action: "新增 / 编辑 / 停用 / 重置密码 / 删除用户"
    },
    {
        area: "role", module: "系统管理", menu: "角色管理", sort: 170,
        page: "进入角色管理", list: "查看角色列表", detail: "查看角色详情",
        action: "新增 / 编辑 / 删除角色并配置权限"
    },
    {
        area: "department", module: "系统管理", menu: "部门管理", sort: 180,
        page: "进入部门管理", list: "查看部门列表", detail: "查看部门详情",
        action: "新增 / 编辑 / 删除部门"
    },
    {
        area: "permission", module: "系统管理", menu: "权限管理", sort: 190,
        page: "进入权限管理", list: "查看权限目录", detail: "查看权限条目详情",
        action: "维护权限目录（新增 / 编辑 / 删除 / 清理）"
    },
    {
        area: "office.overview", module: "综合办公", menu: "财务总览", sort: 200,
        page: "进入综合办公", list: "查看财务总览"
    },
    {
        area: "office.expense", module: "综合办公", menu: "财务报销", sort: 210,
        page: "进入财务报销", list: "查看报销单列表", detail: "查看报销单详情",
        action: "新建 / 编辑 / 提交 / 撤回 / 删除草稿", approve: "审核报销单（通过 / 驳回 / 打款）"
    },
    {
        area: "office.purchase", module: "综合办公", menu: "采购申请", sort: 220,
        page: "进入采购申请", list: "查看采购单列表", detail: "查看采购单详情",
        action: "新建 / 编辑 / 提交 / 撤回 / 删除草稿", approve: "审核采购单"
    },
    {
        area: "office.asset", module: "综合办公", menu: "行政资产", sort: 230,
        page: "进入行政资产", list: "查看资产台账", detail: "查看资产详情",
        action: "登记 / 编辑 / 删除资产", approve: "资产领用 / 归还 / 报废"
    },
    {
        area: "office.attendance", module: "综合办公", menu: "考勤与请假", sort: 240,
        page: "进入考勤与请假", list: "查看请假单列表", detail: "查看请假单详情",
        action: "新建 / 编辑 / 提交 / 撤回 / 删除草稿", approve: "审核请假单"
    },
    {
        area: "office.calendar", module: "综合办公", menu: "办公日历", sort: 250,
        page: "进入办公日历", list: "查看办公日历", detail: "查看日程详情",
        action: "新建 / 编辑 / 删除日程"
    },
    {
        area: "office.payroll", module: "综合办公", menu: "工资发放", sort: 260,
        page: "进入工资发放", list: "查看工资记录", detail: "查看工资明细",
        action: "发起 / 继续发放 / 生成图片并发送 / 删除记录"
    }
];
export const SETTINGS_CONFIG_AREAS = [
    { area: "basic", section: "basic", label: "基础设置", hint: "系统名称、默认时区、Icon / Logo、值班部门、默认 SLA、通知策略、文件存储配额、安全开关与数据保留天数" },
    { area: "attribute_types", section: "attributeTypes", label: "属性类型", hint: "事件与任务的扩展字段（新增字段 / 改字段名 / 选项）" },
    { area: "risk_types", section: "riskTypes", label: "风险类型", hint: "风险事件可选的类型清单" },
    { area: "log_types", section: "logTypes", label: "日志类型", hint: "处理日志的类型与字段" },
    { area: "tags", section: "tags", label: "标签配置", hint: "事件、任务与日志共用的标签" }
];
/**
 * 并回「基础设置」的接口段落：接口还在（PUT /api/settings/<section>），权限不单独占，
 * 一律按 settings.basic.read / settings.basic.write 判。
 */
export const SETTINGS_MERGED_SECTIONS = ["notification", "files", "security", "retention"];
const SETTINGS_MERGED = new Set(SETTINGS_MERGED_SECTIONS);
/** 系统配置单项权限码的形状：settings.<配置项>.<read|write> */
export const SETTINGS_PERMISSION_CODE = /^settings\.([a-z][a-z0-9_]*)\.(read|write)$/;
export function settingsPermissionCode(area, action) {
    return `settings.${area}.${action}`;
}
const SETTINGS_AREA_BY_KEY = (() => {
    const map = new Map();
    for (const item of SETTINGS_CONFIG_AREAS) {
        map.set(item.area, item);
        map.set(item.section.toLowerCase(), item);
    }
    return map;
})();
/** 按接口段落名（basic / riskTypes / logTypes…）或权限码分段（risk_types）找到配置项 */
export function settingsConfigArea(key) {
    return SETTINGS_AREA_BY_KEY.get(String(key ?? "").trim().toLowerCase());
}
/**
 * 接口段落名 / 权限码分段 → 权限码里的那一段：
 * 独立配置项用自己的一段，并回基础设置的四个段落一律落回 basic，未知段落返回 undefined
 * （调用方退回老码 settings.read / settings.write；接口自己的白名单会把它挡成 400）。
 */
export function settingsSectionPermissionArea(section) {
    const key = String(section ?? "").trim().toLowerCase();
    const item = SETTINGS_AREA_BY_KEY.get(key);
    if (item)
        return item.area;
    return SETTINGS_MERGED.has(key) ? "basic" : undefined;
}
/**
 * 系统配置的单项判定（含老码兼容），前后端同一份：
 *   settings.write        → 所有配置项的读写全都有（老角色行为不变）；
 *   settings.read         → 所有配置项的查看；
 *   settings.<项>.write   → 蕴含 settings.<项>.read（写手上一定看得见）。
 * 参数既可以是权限码分段（risk_types），也可以是接口段落名（riskTypes / notification）；
 * 并回基础设置的段落（notification / files / security / retention）按 settings.basic.* 判。
 */
export function grantsSettingsPermission(granted, area, action) {
    const list = granted ?? [];
    if (list.includes("*"))
        return true;
    const key = settingsSectionPermissionArea(area) ?? String(area ?? "");
    if (list.includes(settingsPermissionCode(key, action)))
        return true;
    if (list.includes("settings.write"))
        return true;
    if (action === "read" && (list.includes("settings.read") || list.includes(settingsPermissionCode(key, "write"))))
        return true;
    return false;
}
/** 层 → 展示类型（权限管理 / 角色设置里的分组标签） */
export const PERMISSION_LAYER_LABEL = {
    page: "功能", list: "列表", detail: "详情", action: "操作", approve: "审核"
};
const LAYER_ORDER = ["page", "list", "detail", "action", "approve"];
function codesOf(group) {
    return {
        ...(group.page ? { page: `${group.area}.page` } : {}),
        ...(group.list ? { list: `${group.area}.read` } : {}),
        ...(group.detail ? { detail: `${group.area}.detail` } : {}),
        ...(group.action ? { action: `${group.area}.write` } : {}),
        ...(group.approve ? { approve: group.approveCode ?? `${group.area}.approve` } : {})
    };
}
/** 拍平成「一条权限一个码」的清单：排序 = 菜单基数 + 层序，保证界面上四层按顺序挨着显示 */
export function permissionCatalogEntries() {
    const rows = [];
    for (const group of PERMISSION_MENUS) {
        const codes = codesOf(group);
        for (const layer of LAYER_ORDER) {
            const code = codes[layer];
            if (!code)
                continue;
            const label = group[layer];
            if (!label)
                continue;
            rows.push({
                module: group.module,
                menu: group.menu,
                // 名称加层前缀，勾选时一眼看出是"功能 / 列表 / 详情 / 操作"哪一层
                name: layer === "page" ? label : `${PERMISSION_LAYER_LABEL[layer]}：${label}`,
                code,
                type: layer === "page" ? "menu" : layer === "list" ? "list" : layer === "detail" ? "detail" : "button",
                sort: group.sort + LAYER_ORDER.indexOf(layer),
                description: layer === "page" ? "左侧菜单与页面入口" : undefined
            });
        }
    }
    // 「新建任务」独立成一条（不属于四层）：挂在任务中心菜单下，排序紧跟它的四层之后
    const taskGroup = PERMISSION_MENUS.find(group => group.area === "task");
    if (taskGroup) {
        rows.push({
            module: taskGroup.module, menu: taskGroup.menu,
            name: "新建：新建任务（选类型 / 关联事件 / 附件）",
            code: TASK_CREATE_CODE, type: "button",
            // 排序紧跟任务中心四层（35-38）之后；任务没有审核层，39 是空的，不和 event 的 40 撞
            sort: taskGroup.sort + LAYER_ORDER.length - 1,
            description: "只有勾了这条的人才能新建任务；改进度 / 写日志 / 邀请成员看「操作」那条"
        });
    }
    // 系统配置再按配置项拆成一对读写码，全部挂在「系统管理 / 系统配置」菜单下，
    // 排序紧跟系统配置的四层之后：角色界面里一眼看出这一条属于哪个配置项。
    const settingsGroup = PERMISSION_MENUS.find(group => group.area === "settings");
    if (settingsGroup) {
        SETTINGS_CONFIG_AREAS.forEach((item, index) => {
            const sort = settingsGroup.sort + LAYER_ORDER.length + index * 2;
            rows.push({
                module: settingsGroup.module, menu: settingsGroup.menu,
                name: `${item.label} · 查看`, code: settingsPermissionCode(item.area, "read"),
                type: "list", sort, description: `查看：${item.hint}`
            });
            rows.push({
                module: settingsGroup.module, menu: settingsGroup.menu,
                name: `${item.label} · 编辑`, code: settingsPermissionCode(item.area, "write"),
                type: "button", sort: sort + 1, description: `修改：${item.hint}`
            });
        });
    }
    return rows;
}
/** 权限码 → 所在层 */
export const PERMISSION_LAYER_OF = (() => {
    const map = {};
    for (const entry of permissionCatalogEntries())
        map[entry.code] = entry.type === "menu" ? "page" : entry.type;
    return map;
})();
const CHAINS = (() => {
    const map = new Map();
    for (const group of PERMISSION_MENUS) {
        const codes = codesOf(group);
        const below = [codes.detail, codes.list, codes.page].filter(Boolean);
        // 操作 → 详情 → 列表 → 功能；审核 → 详情 → 列表 → 功能（审核不蕴含操作：只给审核不能改单）
        if (codes.action)
            map.set(codes.action, [codes.action, ...below]);
        if (codes.approve)
            map.set(codes.approve, [codes.approve, ...below]);
        if (codes.detail)
            map.set(codes.detail, [codes.detail, codes.list, codes.page].filter(Boolean));
        if (codes.list)
            map.set(codes.list, [codes.list, codes.page].filter(Boolean));
    }
    // 「新建任务」蕴含详情 / 列表 / 功能（勾了新建就能进任务中心、看得见并打开自己建的任务），
    // 但**不**蕴含 task.write：能新建 ≠ 能改别人（甚至自己）已有的任务。
    const taskCodes = codesOf(PERMISSION_MENUS.find(group => group.area === "task") ?? { area: "task" });
    map.set(TASK_CREATE_CODE, [TASK_CREATE_CODE, taskCodes.detail, taskCodes.list, taskCodes.page].filter(Boolean));
    return map;
})();
/** 拥有这个权限码时，顺带拥有的所有下层权限（含自己） */
export function permissionChain(code) {
    return CHAINS.get(code) ?? [code];
}
/** 给定已授权的权限码，判断是否满足某个权限：支持「上层蕴含下层」 */
export function grantsPermission(granted, permission) {
    const list = granted ?? [];
    if (!permission || list.includes("*") || list.includes(permission))
        return true;
    for (const code of list) {
        const chain = CHAINS.get(code);
        if (chain && chain.includes(permission))
            return true;
    }
    // 系统配置的每个配置项一对读写码：单项判定与老码兼容（settings.read / settings.write）都在这里
    const single = SETTINGS_PERMISSION_CODE.exec(permission);
    if (single)
        return grantsSettingsPermission(list, single[1], single[2]);
    // 「能进系统配置」（settings.page）= 有任意一个配置项的查看权：只被授权某几项的人也能进配置页、
    // 只看到被授权的块。settings.read 故意不这样放宽 —— /api/health/detail、/api/org/summary
    // 还挂在它上面，不能被「只给日志类型」这类窄权限顺带打开。
    if (permission === "settings.page") {
        return SETTINGS_CONFIG_AREAS.some(item => grantsSettingsPermission(list, item.area, "read"));
    }
    return false;
}
export const DATA_SCOPE_OPTIONS = [
    { label: "全部数据", value: "all" },
    { label: "本部门", value: "department" },
    { label: "仅本人相关", value: "self" }
];
/** 可以单独设置数据范围的功能（键即权限区）；其余功能跟随角色默认范围 */
export const DATA_SCOPE_AREAS = [
    { area: "task", module: "任务协作", menu: "任务中心", hint: "任务列表与详情" },
    { area: "event", module: "任务协作", menu: "任务中心", hint: "历史风险事件列表与详情" },
    { area: "approval", module: "审批与审计", menu: "审批", hint: "待审批单据（有审核权限时至少为全部）" },
    { area: "audit", module: "审批与审计", menu: "操作日志", hint: "操作日志列表" },
    { area: "report", module: "数据分析", menu: "统计报表", hint: "报表统计口径" },
    { area: "notice", module: "通知", menu: "公告", hint: "按部门定向的公告" },
    { area: "office.expense", module: "综合办公", menu: "财务报销", hint: "报销单" },
    { area: "office.purchase", module: "综合办公", menu: "采购申请", hint: "采购单" },
    { area: "office.asset", module: "综合办公", menu: "行政资产", hint: "资产台账" },
    { area: "office.attendance", module: "综合办公", menu: "考勤与请假", hint: "请假单" },
    { area: "office.calendar", module: "综合办公", menu: "办公日历", hint: "日程（参与人始终可见）" }
];
export const DATA_SCOPE_AREA_KEYS = DATA_SCOPE_AREAS.map(item => item.area);
export function normalizeDataScope(value) {
    const text = String(value ?? "").trim();
    return text === "all" || text === "department" || text === "self" ? text : "";
}
/** 范围宽窄：多角色合并时取最宽（all > department > self） */
export function widerScope(left, right) {
    const order = { self: 0, department: 1, all: 2 };
    return (order[right] ?? 0) > (order[left] ?? 0) ? right : left;
}
